import React, { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';

import { useAuth } from '@contexts/admin/AuthContext';

import type { Permission } from '@/permissions_manifest.generated';
import {
  getMerchantUserToken,
  setMerchantUserToken,
  clearAllMerchantUserTokens,
  clearAllTokens,
  getPermissionsFromToken,
} from '@utils/merchantUserTokenManager';
import {
  getTabId,
  getMostRecentMerchantSelection,
  setFocusMerchantSelection,
} from '@utils/tabSession';

import adminApiFetch from '@services/admin/apiService';

export interface Merchant {
  id: string;
  name?: string;
  [key: string]: unknown;
}

// Shapes mirror app/controllers/api/admin/v1/common_options_controller.rb#index
export interface ShippingType {
  id: string;
  code: string;
  name: string;
}

export interface Channel {
  id: string;
  code: string;
  extra_name: string | null;
  name: string;
}

export interface Warehouse {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface Currency {
  code: string;
  symbol: string;
  name_key: string;
  decimal_places: number;
}

// shelf_types come from `/shelf_types` (separate endpoint); shape is loose.
export interface ShelfType {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface MerchantContextValue {
  currentMerchant: Merchant | null;
  currentMerchantToken: string | null;
  currentPermissions: Permission[];
  merchants: Merchant[];
  loading: boolean;
  error: string | null;
  requiresSelection: boolean;
  switchMerchant: (merchantId: string) => Promise<void>;
  /** Refresh current merchant details (includes order_item_columns) */
  refreshMerchantInfo: (merchantId?: string) => Promise<Record<string, unknown> | null>;
  refreshMerchants: () => void;
  shippingTypes: ShippingType[];
  channels: Channel[];
  warehouses: Warehouse[];
  defaultWarehouseId: string | null;
  currencies: Currency[];
  /** Organization 體積單位（cbm / cuft），供材積顯示用；隨 common_options 載入、切貨主自動更新 */
  volumeUnit: string | null;
  shelfTypes: ShelfType[];
  availableOrderItemColumns: string[];
  orderItemColumns: string[];
  updateOrderItemColumns: (columns: string[]) => void;
}

interface AuthShape {
  isAuthenticated: boolean;
  user: { id?: string } | null;
  setPermissions?: (permissions: Permission[]) => void;
}

const MerchantContext = createContext<MerchantContextValue | undefined>(undefined);

// 使用 useRef 管理全域快照 - 更簡潔
const globalMerchantRef: { current: Merchant | null } = { current: null };
const globalMerchantTokenRef: { current: string | null } = { current: null };

export const getCurrentMerchantSnapshot = (): Merchant | null => globalMerchantRef.current;
export const getCurrentMerchantTokenSnapshot = (): string | null => globalMerchantTokenRef.current;

interface MerchantProviderProps {
  children: ReactNode;
}

export const MerchantProvider: React.FC<MerchantProviderProps> = ({ children }) => {
  // useAuth lives in a .jsx context — cast to the fields we actually consume.
  const auth = useAuth() as AuthShape;
  const { isAuthenticated, user, setPermissions: setAuthPermissions } = auth;
  const initRef = useRef(false);
  const currentMerchantRef = useRef<Merchant | null>(null);
  const merchantsRef = useRef<Merchant[]>([]); // 新增：merchants 的 ref，避免閉包問題

  // 核心狀態
  const [currentMerchant, setCurrentMerchantState] = useState<Merchant | null>(null);
  const [currentMerchantToken, setCurrentMerchantTokenState] = useState<string | null>(null); // 當前 merchant 的 token
  const [currentPermissions, setCurrentPermissions] = useState<Permission[]>([]); // 當前用戶在此 merchant 的權限
  const [merchants, setMerchantsState] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresSelection, setRequiresSelection] = useState(false);

  // 共用選項數據
  const [shippingTypes, setShippingTypes] = useState<ShippingType[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [defaultWarehouseId, setDefaultWarehouseId] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [volumeUnit, setVolumeUnit] = useState<string | null>(null);
  const [shelfTypes, setShelfTypes] = useState<ShelfType[]>([]);
  const [availableOrderItemColumns, setAvailableOrderItemColumns] = useState<string[]>([]);
  const [orderItemColumns, setOrderItemColumns] = useState<string[]>([]); // Enabled columns

  // Tab ID - 在組件掛載時立即生成（不管是否已認證）
  const [tabId] = useState<string>(() => getTabId());

  // 統一的 setMerchants - 同步更新 state 和 ref
  const setMerchants = (newMerchants: Merchant[]) => {
    setMerchantsState(newMerchants);
    merchantsRef.current = newMerchants;
  };

  // 統一的 setCurrentMerchant - 同步更新所有 refs
  const setCurrentMerchant = (merchant: Merchant | null) => {
    setCurrentMerchantState(merchant);
    currentMerchantRef.current = merchant;
    globalMerchantRef.current = merchant;
  };

  // 統一的 setCurrentMerchantToken - 同步更新 ref 和 permissions
  const setCurrentMerchantToken = (token: string | null) => {
    setCurrentMerchantTokenState(token);
    globalMerchantTokenRef.current = token;

    // 從 token 中解析 permissions
    if (token) {
      const permissions = getPermissionsFromToken(token) as Permission[] | null;
      setCurrentPermissions(permissions || []);
      // 同步到 AuthContext
      if (setAuthPermissions) {
        setAuthPermissions(permissions || []);
      }
    } else {
      setCurrentPermissions([]);
      // 同步到 AuthContext
      if (setAuthPermissions) {
        setAuthPermissions([]);
      }
    }
  };

  // 清理
  useEffect(() => {
    return () => {
      globalMerchantRef.current = null;
      globalMerchantTokenRef.current = null;
      currentMerchantRef.current = null;
    };
  }, []);

  // 載入指定 merchant 詳細資訊（會合併到 currentMerchant，例如 order_item_columns）
  const loadMerchantInfo = async (merchantId?: string): Promise<Record<string, unknown> | null> => {
    try {
      const targetId = merchantId || currentMerchant?.id;
      if (!targetId) return null;
      const resp = await adminApiFetch(`merchants/${targetId}`);
      const result = await resp.json();
      if (result?.success && result?.data) {
        // 分別更新 orderItemColumns 和 availableOrderItemColumns，避免觸發 currentMerchant 更新
        if (result.data.order_item_columns !== undefined) {
          setOrderItemColumns(result.data.order_item_columns || []);
        }
        if (result.data.available_order_item_columns) {
          setAvailableOrderItemColumns(result.data.available_order_item_columns);
        }
        return result.data;
      }
      return null;
    } catch (e) {
      console.error('Failed to load merchant info:', e);
      return null;
    }
  };

  // 從儲存中恢復 merchant - 優先使用最新的全域選擇（檢查是否有 token）
  const restoreMerchantFromStorage = (
    availableMerchants: Merchant[],
  ): { merchant: Merchant; token: string | null } | null => {
    if (!user?.id || !availableMerchants.length) return null;

    const mostRecent = getMostRecentMerchantSelection(user.id);
    if (mostRecent?.merchantId) {
      const merchant = availableMerchants.find(m => m.id === mostRecent.merchantId);
      if (merchant) {
        // 檢查是否有 token
        const token = getMerchantUserToken(user.id, merchant.id);
        return token ? { merchant, token } : null;
      }
    }
    return null;
  };

  // 初始化邏輯
  const initializeMerchants = async () => {
    if (!isAuthenticated || !user?.id || !tabId || initRef.current) {
      return;
    }

    setLoading(true);
    setError(null);
    initRef.current = true;

    try {
      // 使用 user API（此時尚未選擇 merchant，只有 user_token）
      const response = await fetch('/api/user/v1/merchants', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();

      // API 返回的 merchants 直接在 data.data（是個陣列）
      const merchantList: Merchant[] = data.data || [];
      setMerchants(merchantList);

      // 決定當前 merchant
      let selectedMerchant: Merchant | null = null;
      let selectedToken: string | null = null;

      if (merchantList.length === 1) {
        // 只有一個 merchant，自動選擇
        selectedMerchant = merchantList[0];
        // 嘗試獲取 token
        selectedToken = getMerchantUserToken(user.id, selectedMerchant.id);
        if (!selectedToken) {
          // 沒有 token，呼叫 API 獲取
          await switchMerchant(selectedMerchant.id); // 使用 ref，不需要傳參數
          return; // switchMerchant 會設定 state
        }
      } else if (merchantList.length > 1) {
        // 多個 merchant，嘗試恢復上次選擇
        const restored = restoreMerchantFromStorage(merchantList);
        if (restored) {
          selectedMerchant = restored.merchant;
          selectedToken = restored.token;

          // 如果 token 過期，重新獲取
          if (!selectedToken) {
            await switchMerchant(selectedMerchant.id); // 使用 ref，不需要傳參數
            return; // switchMerchant 會設定 state
          }
        }
      }

      if (selectedMerchant && selectedToken) {
        // 先呼叫 get_permissions 取得伺服器端最新權限。
        // Fail-closed：拿不到權限就**不**進入 selected 狀態（不可繼續用 JWT 中的舊 cache，
        // 否則使用者會帶著過期權限操作）。改要求重新選擇 merchant。
        let serverPermissions: Permission[] | null = null;
        try {
          const permissionsResponse = await adminApiFetch('/get_permissions', {
            headers: {
              'Authorization': `Bearer ${selectedToken}`,
            },
          });
          const permissionsResult = await permissionsResponse.json();

          if (permissionsResult.success) {
            serverPermissions = (permissionsResult.data.permissions || []) as Permission[];
          } else {
            console.error('[MerchantContext] get_permissions failed:', permissionsResult.message);
          }
        } catch (permErr) {
          console.error('[MerchantContext] get_permissions error:', permErr);
        }

        if (serverPermissions === null) {
          // Fail closed：清掉這個 merchant 的 cached token（避免下次又拿同一張 stale 的）
          // 並退回 selection 畫面。使用者重新選 merchant 時會再試一次 select_merchant。
          clearAllMerchantUserTokens(user.id);
          setCurrentMerchant(null);
          setCurrentMerchantToken(null);
          setRequiresSelection(merchantList.length > 0);
          setError('permissions_unavailable');
        } else {
          setCurrentMerchant(selectedMerchant);
          setCurrentMerchantToken(selectedToken);
          setRequiresSelection(false);
          // 更新焦點 merchant
          setFocusMerchantSelection(user.id, selectedMerchant.id, tabId);
          // 載入詳細資訊（例如 order_item_columns）
          loadMerchantInfo(selectedMerchant.id);
          // 寫入伺服器最新權限
          setCurrentPermissions(serverPermissions);
          if (setAuthPermissions) {
            setAuthPermissions(serverPermissions);
          }
        }
      } else {
        setCurrentMerchant(null);
        setCurrentMerchantToken(null);
        setRequiresSelection(merchantList.length > 0);
      }
    } catch (err) {
      console.error('Merchant initialization error:', err);
      setError(err instanceof Error ? err.message : String(err));
      setMerchants([]);
      setCurrentMerchant(null);
      setCurrentMerchantToken(null);
      setRequiresSelection(true);
    } finally {
      setLoading(false);
    }
  };

  // 追蹤上一次的認證狀態，用於判斷是否為 logout
  const prevAuthRef = useRef<{ isAuthenticated: boolean | null; userId: string | null | undefined }>({
    isAuthenticated: null,
    userId: null,
  });

  // 主初始化 useEffect
  useEffect(() => {
    const prevAuth = prevAuthRef.current;
    const wasAuthenticated = prevAuth.isAuthenticated === true;

    if (isAuthenticated && user?.id && tabId) {
      initializeMerchants();
    } else {
      // 重置狀態
      setMerchants([]);
      setCurrentMerchant(null);
      setCurrentMerchantToken(null);
      setRequiresSelection(false);
      setLoading(false); // 當未登入時，設置 loading 為 false，讓路由可以正常渲染
      setError(null);
      initRef.current = false;

      // 只在從已認證變為未認證時（真正的 logout）才清除 tokens
      // 避免頁面 refresh 時初始狀態（未認證）誤清除 token
      if (wasAuthenticated) {
        clearAllTokens();
      }
    }

    // 更新前一次的認證狀態
    prevAuthRef.current = { isAuthenticated, userId: user?.id };
  }, [isAuthenticated, user?.id, tabId]);

  // 頁面標題
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = currentMerchant?.name ? `${currentMerchant.name} - GoWarehouse` : 'GoWarehouse';
  }, [currentMerchant?.name]);

  // 監聽視窗焦點，更新焦點 merchant
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !tabId || !currentMerchant) return;

    const handleFocus = () => {
      setFocusMerchantSelection(user.id!, currentMerchant.id, tabId);
    };

    window.addEventListener('focus', handleFocus);
    // 立即執行一次，標記當前tab為焦點
    handleFocus();

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user?.id, tabId, currentMerchant?.id]);

  // 切換 merchant（呼叫 API 獲取 token）
  const switchMerchant = async (merchantId: string): Promise<void> => {
    console.log('[MerchantContext] switchMerchant() 開始, merchantId:', merchantId);
    // 使用 ref 獲取最新的 merchants，避免閉包問題
    const merchant = merchantsRef.current.find(s => s.id === merchantId);
    if (!merchant) {
      console.error('[MerchantContext] ❌ 找不到 merchant, merchantId:', merchantId, 'merchantsRef.current:', merchantsRef.current);
      return;
    }

    console.log('[MerchantContext] 找到 merchant:', merchant);
    setLoading(true);

    try {
      // 先檢查是否有 token（未過期）
      let token = user?.id ? getMerchantUserToken(user.id, merchantId) : null;
      console.log('[MerchantContext] localStorage token:', token ? '存在' : '不存在');

      if (!token) {
        // 沒有 token，呼叫 API 獲取
        console.log('[MerchantContext] 呼叫 POST /api/user/v1/select_merchant...');
        const response = await fetch('/api/user/v1/select_merchant', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ merchant_id: merchantId }),
        });
        console.log('[MerchantContext] API 回應 status:', response.status);
        const result = await response.json();
        console.log('[MerchantContext] API 回應 result:', result);

        if (result.success && result.data) {
          token = result.data.merchant_user_token;
          console.log('[MerchantContext] ✅ 獲得 merchant_user_token');
          // 儲存 token 到 localStorage（按 merchantId）
          if (user?.id && token) {
            setMerchantUserToken(user.id, merchantId, token);
          }
        } else {
          console.error('[MerchantContext] ❌ API 回應失敗:', result.message);
          throw new Error(result.message || 'Failed to select merchant');
        }
      } else {
        console.log('[MerchantContext] ✅ 使用 localStorage 中的 token');
      }

      // 先驗證可拿到伺服器端 permissions，再 commit merchant state（fail-closed）。
      // 不在拿到 permissions 前先 setCurrentMerchant — 否則拿不到 permissions 也已經切過去。
      const permissionsResponse = await adminApiFetch('/get_permissions', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const permissionsResult = await permissionsResponse.json();

      if (!permissionsResult.success) {
        throw new Error(permissionsResult.message || 'Failed to get permissions');
      }

      const permissions: Permission[] = (permissionsResult.data.permissions || []) as Permission[];

      // 設定當前 merchant 和 token（這會觸發 loadCommonOptions）
      setCurrentMerchant(merchant);
      setCurrentMerchantToken(token);
      setRequiresSelection(false);

      // 載入詳細資訊（例如 order_item_columns）
      loadMerchantInfo(merchant.id);

      // 更新焦點 merchant（用於新 tab 初始化）
      if (user?.id && tabId) {
        setFocusMerchantSelection(user.id, merchant.id, tabId);
      }

      setCurrentPermissions(permissions);
      if (setAuthPermissions) {
        setAuthPermissions(permissions);
      }

    } catch (err) {
      console.error('Failed to switch merchant:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // 載入共用選項數據
  const loadCommonOptions = async () => {
    if (!isAuthenticated || !currentMerchant) return;

    try {
      const [commonOptionsResponse, shelfTypesResponse] = await Promise.all([
        adminApiFetch('/common_options'),
        adminApiFetch('/shelf_types'),
      ]);

      const commonOptionsResult = await commonOptionsResponse.json();
      const shelfTypesResult = await shelfTypesResponse.json();

      if (commonOptionsResult.success && commonOptionsResult.data) {
        setShippingTypes(commonOptionsResult.data.shipping_types || []);
        setChannels(commonOptionsResult.data.channels || []);
        setWarehouses(commonOptionsResult.data.warehouses || []);
        setDefaultWarehouseId(commonOptionsResult.data.default_warehouse_id || null);
        setCurrencies(commonOptionsResult.data.currencies || []);
        setVolumeUnit(commonOptionsResult.data.volume_unit || null);
      }

      if (shelfTypesResult.success && shelfTypesResult.data) {
        setShelfTypes(shelfTypesResult.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch common options:', err);
    }
  };

  // 當 currentMerchant 改變時載入共用選項
  useEffect(() => {
    if (currentMerchant) {
      loadCommonOptions();
    } else {
      // 清空選項數據
      setShippingTypes([]);
      setChannels([]);
      setWarehouses([]);
      setDefaultWarehouseId(null);
      setCurrencies([]);
      setVolumeUnit(null);
      setShelfTypes([]);
      setAvailableOrderItemColumns([]);
      setOrderItemColumns([]);
    }
  }, [currentMerchant?.id, isAuthenticated]);

  // 重新載入 merchants
  const refreshMerchants = () => {
    initRef.current = false;
    initializeMerchants();
  };

  // 更新 orderItemColumns（供 useOrderItemColumns Hook 使用）
  const updateOrderItemColumns = (columns: string[]) => {
    setOrderItemColumns(columns);
  };

  const value: MerchantContextValue = {
    currentMerchant,
    currentMerchantToken,
    currentPermissions,
    merchants,
    loading,
    error,
    requiresSelection,
    switchMerchant,
    // Refresh current merchant details (includes order_item_columns)
    refreshMerchantInfo: loadMerchantInfo,
    refreshMerchants,
    shippingTypes,
    channels,
    warehouses,
    defaultWarehouseId,
    currencies,
    volumeUnit,
    shelfTypes,
    availableOrderItemColumns,
    orderItemColumns,
    updateOrderItemColumns,
  };

  return (
    <MerchantContext.Provider value={value}>
      {children}
    </MerchantContext.Provider>
  );
};

export const useMerchant = (): MerchantContextValue => {
  const context = useContext(MerchantContext);
  if (!context) {
    throw new Error('useMerchant must be used within a MerchantProvider');
  }
  return context;
};

export default MerchantContext;
